import React, { useEffect, useRef, useState } from 'react';
import p from '../../styles/renewal/OnePage.module.css';
import { COMPANY } from './data';
import useMediaQuery from './useMediaQuery';

const TEL = `tel:${COMPANY.phone.replace(/-/g, '')}`;

const PhoneIcon = () => (
  <svg className={p.fabIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M6.6 10.8c1.2 2.4 3.2 4.4 5.6 5.6l1.9-1.9c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1V19c0 .6-.4 1-1 1-8.3 0-15-6.7-15-15 0-.6.4-1 1-1h3.1c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1l-1.9 1.9z"
    />
  </svg>
);

const KakaoIcon = () => (
  <svg className={p.fabIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M12 3C6.9 3 2.8 6.2 2.8 10.2c0 2.5 1.7 4.7 4.2 6l-1 3.6c-.1.3.2.5.5.4l4.3-2.8c.4 0 .8.1 1.2.1 5.1 0 9.2-3.2 9.2-7.2S17.1 3 12 3z"
    />
  </svg>
);

const TopIcon = () => (
  <svg className={p.fabIcon} viewBox="0 0 24 24" aria-hidden="true">
    <path fill="currentColor" d="M12 5.8l6.2 6.2-1.41 1.41L13 9.63V19h-2V9.63l-3.79 3.58L5.8 12z" />
  </svg>
);

const goTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

/**
 * 데스크톱: 우하단 원형 버튼 4개 (전화는 번호 복사 말풍선)
 * 모바일: 하단 고정 액션 바 + 스크롤 후 나타나는 TOP 버튼 (전화는 바로 걸기)
 */
const Floating = ({ onNavigate, hidden }) => {
  const isMobile = useMediaQuery('(max-width: 640px)');
  const [scrolled, setScrolled] = useState(false);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 500);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (hidden) setPhoneOpen(false);
  }, [hidden]);

  // ── 모바일: 하단 고정 바 ──────────────────────────────────
  if (isMobile) {
    return (
      <>
        {scrolled ? (
          <button type="button" className={p.topFab} title="맨 위로" onClick={goTop}>
            <TopIcon />
            <span className={p.srOnly}>맨 위로</span>
          </button>
        ) : null}

        <div className={p.bar}>
          <a className={p.barItem} href={TEL}>
            <PhoneIcon />
            전화 상담
          </a>
          <a
            className={p.barItem}
            href={COMPANY.kakao}
            target="_blank"
            rel="noopener noreferrer"
          >
            <KakaoIcon />
            카톡 상담
          </a>
          <button type="button" className={p.barCta} onClick={() => onNavigate('contact')}>
            문의 신청 →
          </button>
        </div>
      </>
    );
  }

  // ── 데스크톱: 우하단 원형 버튼 ────────────────────────────
  const copyPhone = async () => {
    const done = () => {
      setCopied(true);
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1800);
    };

    try {
      await navigator.clipboard.writeText(COMPANY.phone);
    } catch (err) {
      // clipboard API 가 막힌 환경(비보안 컨텍스트 등)에서는 임시 textarea 로 대체
      const ta = document.createElement('textarea');
      ta.value = COMPANY.phone;
      ta.setAttribute('readonly', '');
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
      } catch (e) {
        /* 복사 불가 환경 — 번호는 화면에 그대로 노출되어 있다 */
      }
      document.body.removeChild(ta);
    }
    done();
  };

  return (
    <div className={`${p.floating} ${hidden ? p.floatingHidden : ''}`} aria-hidden={hidden}>
      <button type="button" className={`${p.fab} ${p.fabTop}`} title="맨 위로" onClick={goTop}>
        <TopIcon />
        <span className={p.fabLabel} translate="no">TOP</span>
      </button>

      <div className={p.floatWrap}>
        {phoneOpen ? (
          <div className={p.phonePop}>
            <div className={p.phonePopLabel}>고객센터</div>
            <button type="button" className={p.phonePopNumber} onClick={copyPhone}>
              {COMPANY.phone}
            </button>
            {copied ? (
              <div className={p.phonePopDone} role="status">
                번호가 복사되었습니다
              </div>
            ) : (
              <div className={p.phonePopHint}>번호를 누르면 복사됩니다</div>
            )}
            <div className={p.phonePopArrow} aria-hidden="true" />
          </div>
        ) : null}

        <button
          type="button"
          className={`${p.fab} ${p.fabDark}`}
          title="전화 문의"
          aria-expanded={phoneOpen}
          onClick={() => setPhoneOpen((v) => !v)}
        >
          <PhoneIcon />
          <span className={p.fabLabel}>전화</span>
        </button>
      </div>

      <button
        type="button"
        className={`${p.fab} ${p.fabPrimary}`}
        title="문의하기"
        onClick={() => onNavigate('contact')}
      >
        <span className={p.fabGlyph} aria-hidden="true">
          ✎
        </span>
        <span className={p.fabLabel}>문의</span>
      </button>

      <a
        className={`${p.fab} ${p.fabKakao}`}
        href={COMPANY.kakao}
        target="_blank"
        rel="noopener noreferrer"
        title="카카오톡 상담"
      >
        <KakaoIcon />
        <span className={p.fabLabel}>상담</span>
      </a>
    </div>
  );
};

export default Floating;
